/**
 * Task 11 — SSN input. Reusable. Drop-in for any form that captures a
 * guarantor SSN.
 *
 * Behavior:
 *   * Live formats ``XXX-XX-XXXX`` as the user types.
 *   * On blur: visually replaces the first 5 digits with ``\u2022``
 *     while keeping the underlying value (digits) in the parent.
 *   * On focus: shows the editable formatted form again.
 *   * ``inputMode="numeric"`` + ``autoComplete="off"`` + ``pattern="[0-9]*"``.
 *   * Inline validation error renders below the input when invalid +
 *     touched (uses ``lib/ssn.js::validateSsn``).
 *
 * Props:
 *   * ``value``         — controlled string (digits or formatted; the
 *                         component normalises internally).
 *   * ``onChange(next)`` — emits the raw formatted string (``XXX-XX-XXXX``)
 *                         so the parent can submit it. Backend strips
 *                         dashes; both shapes are accepted server-side.
 *   * ``onValidityChange?(isValid, error)`` — optional bubble so the
 *                         parent can disable submit while invalid.
 *   * ``disabled``      — pass-through.
 *   * ``placeholder``   — defaults to ``XXX-XX-XXXX``.
 *   * ``maskedLast4``   — when supplied AND the input is empty + not
 *                         focused, render ``\u2022\u2022\u2022-\u2022\u2022-LLLL``
 *                         (existing-record view).
 *   * ``data-testid``   — pass-through.
 */
import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import {
    SSN_MASK_CHAR,
    formatSsn,
    isMaskedSsn,
    maskSsn,
    ssnToDigits,
    validateSsn,
} from "@/lib/ssn";

export default function SSNInput({
    value,
    onChange,
    onValidityChange,
    disabled = false,
    placeholder = "XXX-XX-XXXX",
    maskedLast4 = null,
    "data-testid": testId = "ssn-input",
    className = "",
    autoFocus = false,
}) {
    const [focused, setFocused] = useState(false);
    const [touched, setTouched] = useState(false);

    // Internal digits string drives validation.
    const digits = isMaskedSsn(value) ? "" : ssnToDigits(value);
    const formatted = formatSsn(digits);
    const error = validateSsn(digits);

    useEffect(() => {
        if (onValidityChange) onValidityChange(!error, error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    // Display value:
    //   * focused → formatted (editable)
    //   * blurred + 9 digits entered → mask first 5
    //   * blurred + empty + maskedLast4 provided → render the existing-
    //     record mask so the form clearly shows "an SSN is on file"
    //   * blurred + partial → keep the formatted partial visible so
    //     the user sees what they typed
    let display = formatted;
    if (!focused) {
        if (digits.length === 9) {
            display = maskSsn(digits);
        } else if (digits.length === 0 && maskedLast4) {
            display = `${SSN_MASK_CHAR.repeat(3)}-${SSN_MASK_CHAR.repeat(2)}-${maskedLast4}`;
        }
    }

    function handleChange(e) {
        const next = ssnToDigits(e.target.value).slice(0, 9);
        // Always emit the formatted form so submit handlers don't have
        // to do their own normalization.
        onChange(formatSsn(next));
    }

    return (
        <div className="space-y-1">
            <Input
                data-testid={testId}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                pattern="[0-9]*"
                disabled={disabled}
                placeholder={placeholder}
                value={display}
                onChange={handleChange}
                onFocus={() => setFocused(true)}
                onBlur={() => { setFocused(false); setTouched(true); }}
                autoFocus={autoFocus}
                className={`tracking-widest ${className}`}
                aria-invalid={touched && !!error || undefined}
                maxLength={11}  // XXX-XX-XXXX
            />
            {touched && error && digits.length > 0 && (
                <p className="text-xs text-red-600" data-testid={`${testId}-error`}>
                    {error}
                </p>
            )}
        </div>
    );
}
